/**
 * Extract and format error messages from API error responses
 * Handles various error response formats from different APIs
 */
export const extractErrorMessage = (error) => {
  // If it's already a string, check if it's a stringified Python error
  if (typeof error === 'string') {
    const extracted = extractFromStringified(error);
    if (extracted) return extracted;
    return error;
  }

  // Check error.message first (sometimes errors are wrapped)
  if (error.message && typeof error.message === 'string') {
    const extracted = extractFromStringified(error.message);
    if (extracted) return extracted;
  }

  // Handle axios error response
  if (error.response && error.response.data) {
    const data = error.response.data;
    
    // If data is a string, try to extract from it
    if (typeof data === 'string') {
      const extracted = extractFromStringified(data);
      if (extracted) return extracted;
      return data;
    }

    // Try common error field names first
    if (data.message && typeof data.message === 'string') {
      // Try to extract from stringified message
      const extracted = extractFromStringified(data.message);
      if (extracted) {
        return extracted;
      }
      return data.message;
    }
    if (data.detail && typeof data.detail === 'string') {
      return data.detail;
    }
    if (data.error && typeof data.error === 'string') {
      return data.error;
    }

    // Handle non_field_errors (Django REST Framework format)
    if (data.non_field_errors && Array.isArray(data.non_field_errors)) {
      const messages = data.non_field_errors.map(err => {
        // Handle string errors
        if (typeof err === 'string') {
          return err;
        }
        // Handle ErrorDetail objects with .string property
        if (err && typeof err === 'object' && err.string) {
          return err.string;
        }
        // Handle other object formats
        if (err && typeof err === 'object') {
          if (err.message) return err.message;
          return String(err);
        }
        // Fallback
        return String(err);
      });
      if (messages.length > 0) {
        return messages.join(', ');
      }
    }

    // Handle field-specific errors
    if (typeof data === 'object' && Object.keys(data).length > 0) {
      const fieldErrors = [];
      for (const [field, messages] of Object.entries(data)) {
        // Skip non-error fields
        if (field === 'success' || field === 'status') continue;
        
        if (Array.isArray(messages)) {
          messages.forEach(msg => {
            let errorText;
            if (typeof msg === 'string') {
              errorText = msg;
            } else if (msg && typeof msg === 'object') {
              if (msg.string) {
                errorText = msg.string;
              } else if (msg.message) {
                errorText = msg.message;
              } else {
                errorText = String(msg);
              }
            } else {
              errorText = String(msg);
            }
            if (errorText && errorText.trim()) {
              fieldErrors.push(errorText);
            }
          });
        } else if (typeof messages === 'string') {
          fieldErrors.push(messages);
        } else if (messages && typeof messages === 'object') {
          if (messages.string) {
            fieldErrors.push(messages.string);
          } else if (messages.message) {
            fieldErrors.push(messages.message);
          }
        }
      }
      if (fieldErrors.length > 0) {
        return fieldErrors.join(', ');
      }
    }
  }

  // Handle network errors
  if (error.code === 'ERR_NETWORK') {
    return 'Network error. Please check your connection and try again.';
  }

  if (error.message === 'Network Error') {
    return 'Network error. Please ensure the backend server is running.';
  }

  // Handle timeout errors
  if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
    return 'Request timeout. Please try again.';
  }

  // Fallback to error message
  if (error.message && typeof error.message === 'string') {
    return error.message;
  }

  // Last resort fallback
  return 'An error occurred. Please try again.';
};

/**
 * Extract error message from stringified Python error representation
 * Handles formats like: "{'non_field_errors': [ErrorDetail(string='msg', code='code')]}"
 */
function extractFromStringified(str) {
  if (!str || typeof str !== 'string' || str.length === 0) {
    return null;
  }

  // PATTERN 1: ErrorDetail(string='message', code='...')
  let match = str.match(/ErrorDetail\s*\(\s*string\s*=\s*'([^']*)'/);
  if (match && match[1] && match[1].trim()) {
    return match[1];
  }

  // PATTERN 2: ErrorDetail(string="message", code="...")
  match = str.match(/ErrorDetail\s*\(\s*string\s*=\s*"([^"]*)"/);
  if (match && match[1] && match[1].trim()) {
    return match[1];
  }

  // PATTERN 3: non_field_errors with single quotes
  match = str.match(/non_field_errors['"]?\s*[\s:]*\[*\s*ErrorDetail\s*\(\s*string\s*=\s*'([^']*)'/);
  if (match && match[1] && match[1].trim()) {
    return match[1];
  }

  // PATTERN 4: non_field_errors with double quotes   
  match = str.match(/non_field_errors['"]?\s*[\s:]*\[*\s*ErrorDetail\s*\(\s*string\s*=\s*"([^"]*)"/);
  if (match && match[1] && match[1].trim()) {
    return match[1];
  }

  // PATTERN 5: Simple no non_field_errors array - just ErrorDetail at start
  match = str.match(/^.*?ErrorDetail\s*\(\s*string\s*=\s*['"]([^'"]*)['"]/);
  if (match && match[1] && match[1].trim()) {
    return match[1];
  }

  // PATTERN 6: Look for text between quotes that comes after 'string'
  match = str.match(/string\s*[=:]\s*['"]([^'"]{5,})['"]/);
  if (match && match[1] && match[1].trim()) {
    return match[1];
  }

  // PATTERN 7: Last resort - look for any reasonable error message between quotes
  // Find the longest quoted string in the error
  const allQuoted = str.match(/['"]([^'"]{8,})['"]/g);
  if (allQuoted && allQuoted.length > 0) {
    // Get the longest one (likely the error message)
    const longest = allQuoted.reduce((a, b) => a.length > b.length ? a : b);
    const extracted = longest.match(/['"]([^'"]*)['"]/);
    if (extracted && extracted[1]) {
      return extracted[1];
    }
  }

  return null;
}
